// File: app/src/hooks/useRepEstimate.js
//
// Per-unit / total duration estimate for a single counted Work node (unit
// "reps" or "cal"). Pulls the user's training_data + aggregated exercise
// history so the estimate matches what the workout compiler will use at
// runtime. Mirrors the wiring pattern of useTotalDuration so call sites don't
// need to thread context.

import { useMemo } from "react";
import { useUserProfile } from "../contexts/UserProfileContext";
import { estimatePerCalSec, estimatePerRepSec } from "../utils/repEstimator";
import { isCalorieUnit, isCountedUnit } from "../utils/planNodeUtils";
import { deriveTrainingData } from "../utils/performanceProfile";
import useExerciseHistory from "./useExerciseHistory";

/**
 * Estimate the total seconds a counted Work node will consume at runtime.
 * Returns null for nodes that aren't counted or have no target.
 *
 * `perRepSec` keeps its name for the call sites that read it, but holds
 * seconds per UNIT — per rep for a rep node, per calorie for a cal one.
 */
const useRepEstimate = (node) => {
  const { profile, performanceCapabilities } = useUserProfile();
  const historyMap = useExerciseHistory();
  return useMemo(() => {
    if (!node || !isCountedUnit(node.unit)) return null;
    const count = Number(node.value);
    if (!Number.isFinite(count) || count <= 0) return null;
    const trainingData = deriveTrainingData(performanceCapabilities, {
      pace: profile?.training_data?.pace,
      fallbackTrainingData: profile?.training_data,
    });
    const cal = isCalorieUnit(node.unit);
    const est = cal
      ? estimatePerCalSec(node, trainingData, historyMap || null)
      : estimatePerRepSec(node, trainingData, historyMap || null);
    const perUnitSec = cal ? est.perCalSec : est.perRepSec;
    return {
      perRepSec: perUnitSec,
      totalSec: perUnitSec * count,
      source: est.source,
      loadPct: est.loadPct,
      unit: cal ? "cal" : "reps",
    };
  }, [node, profile, performanceCapabilities, historyMap]);
};

export default useRepEstimate;
